// ./src/components/features/Travels/TravelTypeMultiSelector/TravelTypeMultiSelector.tsx
//
// Multi-select travel type filter using Chip buttons.
// Allows selecting multiple travel types simultaneously.
// Follows the same chip styling pattern as TravelTypeSelector.

import React, { useMemo, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  TRAVEL_TYPE_OPTIONS,
  TravelTypeValue,
} from "@/config/travelTypes";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TravelTypeMultiSelectorProps {
  /** Currently selected travel type values */
  value?: TravelTypeValue[];

  /** Callback fired when selection changes */
  onChange?: (values: TravelTypeValue[]) => void;

  /** Whether the field is required (at least one must be selected) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the input wrapper */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Chip variant */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Styles (matching TravelTypeSelector)
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const TravelTypeMultiSelectorComponent: React.FC<TravelTypeMultiSelectorProps> = ({
  value = [],
  onChange,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  variant = "outline",
}) => {
  const { t } = useTranslation(["travels", "common"]);

  const defaultLabel = label ?? t("travels:selectors.travelType.label");

  // Handle chip group change
  const handleChange = useCallback(
    (selectedValues: string[]) => {
      // If required, prevent deselecting all
      if (required && selectedValues.length === 0) return;
      onChange?.(selectedValues as TravelTypeValue[]);
    },
    [required, onChange]
  );

  // Build chip elements
  const chips = useMemo(() => {
    return TRAVEL_TYPE_OPTIONS.map((config) => {
      const translatedLabel = t(config.translationKey, {
        defaultValue: config.value,
      });

      return (
        <Chip
          key={config.value}
          value={config.value}
          color={config.color}
          variant={variant}
          size="sm"
          disabled={disabled}
          icon={<AppIcon iconName={config.icon} size={20} />}
          styles={{
            label: CHIP_LABEL_STYLES,
            iconWrapper: CHIP_ICON_WRAPPER_STYLES,
            root: CHIP_ROOT_STYLES,
          }}
        >
          {translatedLabel}
        </Chip>
      );
    });
  }, [t, variant, disabled]);

  return (
    <Input.Wrapper
      label={defaultLabel}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={4} style={GROUP_STYLES}>
        <Chip.Group
          multiple
          value={value}
          onChange={handleChange}
        >
          {chips}
        </Chip.Group>
      </Box>
    </Input.Wrapper>
  );
};

export const TravelTypeMultiSelector = React.memo(TravelTypeMultiSelectorComponent);